/**
 * 전체화면 iframe 레이어 ($j.Ui.FullFrame).
 *
 * 다른 페이지를 통째로 얹어 쓰는 화면들 — 주소검색 지도(KakaoMap), 이미지 편집기(ImageEditor),
 * 접수종료 리뷰 지도 — 이 셋이 각자 같은 마크업을 따로 문자열로 만들어 body 에 붙이고 있었다.
 * 높이는 셋 다 `$(window).height()` 를 px 로 박아서 회전하면 어긋났고, 닫는 방식도
 * `.remove()` / `.hide()` 로 제각각이라 재진입 시 거동이 달랐다. 한 곳으로 모은다.
 *
 *   const id = $j.Ui.FullFrame.open('/m/map?gps=...', { id: G5['MAP_ID'] });
 *   $j.Ui.FullFrame.close(id);
 *
 * iframe 안(자식 문서)에서 자기 자신을 닫을 때는 부모의 인스턴스를 잡아야 한다:
 *   $j.Ui.FullFrame.parent().close('map_id')
 * parent() 는 iframe 이 아니면 자기 자신을 돌려주므로 양쪽에서 같은 코드가 돈다.
 *
 * $j.Ui.Modal 의 mode:'full' 과는 쓰임이 다르다. 저쪽은 헤더·닫기버튼을 엔진이 그려주는
 * HTML 컨텐츠용이고, 여기 실리는 페이지들은 자체 헤더(뒤로가기 등)를 이미 갖고 있어
 * 크롬이 겹친다. z-index 도 팝오버/줌(1065~)보다 위(1075)여야 지도가 가려지지 않는다.
 *
 * 스타일은 scss 가 아니라 여기서 인라인으로 박는다 —
 * main.js 는 전 사이트가 로드하지만 사이트 번들 css(dist/m.css·adm.css)를 안 쓰는
 * 레거시 단독 페이지가 있다(`/u/` 접수·기사급여 등 — blade 안의 <style> 과
 * 페이지 전용 css 만 로드한다).
 * 클래스에 스타일을 두면 그런 페이지에서 지도·이미지편집기가 크기 없이 붙어버린다.
 * 높이를 예전처럼 $(window).height() px 로 박지 않고 inset:0 으로 잡는 건 그대로 —
 * 화면 회전이나 주소창 접힘에 자동으로 따라간다.
 *
 * panel 옵션 — 전체화면 대신 화면 가운데 창으로 띄운다:
 *   $j.F.KakaoMap.open(gps, '0', '', { panel: true })
 *   $j.Ui.FullFrame.open(url, { panel: {width: 700, height: 600} })
 * PC 관리자 화면은 1250px 폭에 정보가 빼곡해서 지도가 화면을 다 덮으면 편집중이던
 * 내용이 통째로 가린다. 가운데 패널로 띄워 뒤쪽 폼이 계속 보이게 한다.
 */
const DEFAULT_ID = 'jb-fullframe';

// z-index/배경은 CSS 변수 + 폴백 (관리자 변수셋에는 fullscreen 슬롯이 없다)
const Z_INDEX = 'z-index:var(--z-index-fullscreen,1075);';
const BACKGROUND = 'background:var(--color-bg-base,#fff);';
// 상태바·홈 인디케이터 띠 — iOS 앱 웹뷰는 화면 끝까지 그리는데, env(safe-area-inset-*) 는 iframe 안쪽
// 문서에는 오지 않는다. 그래서 실린 페이지(지도 등)가 스스로 비울 수 없어 레이어가 대신 비운다.
// 값은 사이트가 --ui-fullframe-*-inset 으로 준다(모바일은 글자크기 배율까지 감안한 값). 기본 0 = 현행.
const SAFE_INSET = 'box-sizing:border-box;'
    + 'padding-top:var(--ui-fullframe-top-inset,0px);padding-bottom:var(--ui-fullframe-bottom-inset,0px);';
const LAYER_STYLE = 'position:fixed;inset:0;' + SAFE_INSET + Z_INDEX + BACKGROUND;
const FRAME_STYLE = 'display:block;width:100%;height:100%;border:0;';

const PANEL_SIZE   = {width: 900, height: 700};   // 패널 기본 크기
const PANEL_MARGIN = 20;                          // 뷰포트 가장자리 최소 여백

// 레이어 위에 얹는 닫기(×). iframe 보다 위에 떠야 하므로 z-index 를 따로 준다.
const CLOSE_BTN_STYLE = 'position:absolute;top:calc(6px + var(--ui-fullframe-top-inset,0px));right:6px;z-index:1;'
    + 'width:30px;height:30px;padding:0;border:1px solid #adb5bd;border-radius:50%;'
    + 'background:rgba(255,255,255,.94);color:#212529;font-size:20px;line-height:1;cursor:pointer;'
    + 'box-shadow:0 1px 4px rgba(0,0,0,.3);';

/** 화면 정가운데 고정 패널 — 크기는 min() 으로 뷰포트를 넘지 않게 깎는다(리사이즈 재계산 불필요)
 *
 * 글자크기 확대(zoom) 대응 — 사이트마다 zoom 변수 이름이 다르다 — 관리자 --adm-zoom · 모바일 --fs-zoom.
 * 각 사이트는 제 것만 정의하므로 둘 다 나눠도 안전하다(나머지는 폴백 1).
 * 악덕은 body 가 아니라 .b-scroll 에 zoom 을 걸어 둘 다 미정의 → 값이 그대로다.
 *
 * PANEL_MARGIN 은 **화면 가장자리 여백**이라 뷰포트 단위와 함께 식 전체를 나눈다. 그래야
 * 렌더 결과가 `100vw - 40px` 이 되어 배율과 무관하게 여백이 늘 20px 로 보인다.
 * (반대로 '같이 커지는 크롬'을 빼는 자리라면 뷰포트 단위만 나눠야 한다 — base/_fontsize.scss) */
function panelStyle(size) {
    const {width, height} = Object.assign({}, PANEL_SIZE, size === true ? null : size);
    return 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);' + Z_INDEX + BACKGROUND
        + `width:min(${width}px, calc((100vw - ${PANEL_MARGIN * 2}px) / var(--adm-zoom, 1) / var(--fs-zoom, 1)));`
        + `height:min(${height}px, calc((100vh - ${PANEL_MARGIN * 2}px) / var(--adm-zoom, 1) / var(--fs-zoom, 1)));`
        + 'border:1px solid #808080;box-shadow:0 6px 24px rgba(0,0,0,.4);overflow:hidden;';
}

export default class FullFrame {
    constructor() {
        this.opened = [];   // 열린 순서 — close() 를 id 없이 부르면 마지막 것을 닫는다
    }

    /**
     * @param {string} url    iframe 에 띄울 주소
     * @param {object} [opts] id: 레이어 element id (기존 셀렉터 호환용으로 지정 가능)
     *                        frameId: iframe element id — 바깥에서 contentWindow 를 잡아야 할 때
     *                                 (예: 앱 GPS 갱신 시 열린 지도를 움직이는 public/js/mobile.js)
     *                        title: iframe title 속성(스크린리더)
     *                        panel: true 또는 {width, height} — 전체화면 대신 화면 가운데 창으로
     *                        closeBtn: true — 레이어 우상단에 × 를 얹는다. 실린 페이지가 모바일용이라
     *                                 PC 에서 닫는 길(뒤로가기 등)이 눈에 안 띌 때 쓴다
     * @return {string} 레이어 id
     */
    open(url, opts = {}) {
        const id = opts.id || DEFAULT_ID;
        this.close(id);   // 같은 id 가 떠 있으면 교체 — 재진입 시 옛 내용이 남지 않게

        const $el = $('<div class="jb-fullframe"><iframe></iframe></div>')
            .attr('id', id)
            .attr('style', opts.panel ? panelStyle(opts.panel) : LAYER_STYLE);
        $el.find('iframe')
            .attr('id', opts.frameId || (id + '_frame'))
            .attr('title', opts.title || '')
            .attr('style', FRAME_STYLE)
            .attr('src', url);

        // closeBtn — 실린 페이지가 모바일용이라 PC 에서 닫는 길이 안 보일 때 (관리자 패널 등).
        // 안쪽 문서는 다른 문서라 이 버튼을 그려줄 수 없어 레이어 쪽에서 얹는다.
        if (opts.closeBtn) {
            $('<button type="button" aria-label="닫기">×</button>')
                .attr('style', CLOSE_BTN_STYLE)
                .on('click', () => this.close(id))
                .appendTo($el);
        }

        $('body').append($el);
        this.opened.push(id);
        return id;
    }

    /** @param {string} [id] 생략하면 마지막으로 연 레이어 */
    close(id) {
        const target = id || this.opened[this.opened.length - 1];
        if (!target) return;
        $('#' + target).remove();
        this.opened = this.opened.filter((v) => v !== target);
    }

    closeAll() {
        this.opened.slice().forEach((id) => this.close(id));
    }

    isOpen(id) {
        return $('#' + (id || DEFAULT_ID)).length > 0;
    }

    /**
     * 레이어를 실제로 소유한 창의 인스턴스.
     * iframe 안에서 부르면 부모 것, 아니면 자기 자신 — 자식 문서가 분기 없이 자기를 닫을 수 있다.
     * (크로스 오리진이면 접근이 막히므로 자기 자신으로 폴백)
     */
    parent() {
        try {
            if (window.parent && window.parent !== window && window.parent.$j && window.parent.$j.Ui.FullFrame) {
                return window.parent.$j.Ui.FullFrame;
            }
        } catch (e) { /* cross-origin — 자기 자신으로 */ }
        return this;
    }
}
